import React, { useEffect, useState } from 'react';
import { CancelButton, SubmitButton, EditButton } from '../../../../../components/mui/Buttons/Buttons';
import { Form, FormikProvider, useFormik } from 'formik';
import { Alert, Box, Divider, FormControlLabel, Stack, Switch, TextField, Typography } from '@mui/material';
import GlobalDialog from '../../../../../components/mui/Dialogue/GlobalDialog';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import { addRetention, getRetentionRateInfo } from '../../../../../redux/features/CRDslice';

const validationSchema = Yup.object().shape({
  extraSessions: Yup.number().required('Required').positive('Must be greater than 0').integer('Must be a whole number'),
  extraSaleAmount: Yup.number().required('Required').positive('Must be greater than 0'),
});

// Whole-number equal split, remainder to the first subjects: 10 over 3 -> 4, 3, 3 (same rule as the backend).
const equalSplit = (count, extra) => {
  if (!count) return [];
  const base = Math.floor(extra / count);
  const remainder = extra % count;
  return Array.from({ length: count }, (_, i) => base + (i < remainder ? 1 : 0));
};

const AddRetention = ({ row, dispatchParams, apiCall }) => {
  const [open, setOpen] = useState(false);
  const [rateInfo, setRateInfo] = useState(null);
  const [rateLoading, setRateLoading] = useState(false);
  const [distributeEqually, setDistributeEqually] = useState(false); // off by default: the user places the sessions
  const [manualSessions, setManualSessions] = useState({}); // subject_enrollment_id -> typed whole number (manual mode)
  const [updateFeePerSession, setUpdateFeePerSession] = useState(false); // opt-in: rewrite the stored fee per session
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { loading } = useSelector((state) => state.crd);

  const isAedPriced = rateInfo && !rateInfo.supported;
  const subjects = rateInfo?.subjects || [];
  const canChooseSplit = subjects.length > 1; // one subject gets everything - nothing to choose
  const equalMode = distributeEqually || !canChooseSplit;

  // Sessions each subject receives for a given Extra Sessions figure; the preview and the submit use the same numbers.
  const buildAllocation = (extra) =>
    equalMode
      ? equalSplit(subjects.length, extra)
      : subjects.map((s) => Number(manualSessions[s.subject_enrollment_id] || 0));

  const handleToggleEqual = (checked) => {
    setDistributeEqually(checked);
    if (!checked) {
      // start the manual boxes from the equal split so the user only has to nudge them
      const extra = Number(formik.values.extraSessions);
      const start = Number.isInteger(extra) && extra > 0 ? equalSplit(subjects.length, extra) : subjects.map(() => 0);
      setManualSessions(Object.fromEntries(subjects.map((s, i) => [s.subject_enrollment_id, String(start[i])])));
    }
  };

  useEffect(() => {
    if (open && row?.course_enrollment_id) {
      setDistributeEqually(false);
      setManualSessions({});
      setUpdateFeePerSession(false);
      setRateLoading(true);
      dispatch(getRetentionRateInfo(row.course_enrollment_id))
        .then((res) => {
          if (res.payload?.status === 'success') {
            setRateInfo(res.payload.data);
          }
        })
        .finally(() => setRateLoading(false));
    }
  }, [open, row?.course_enrollment_id]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      course_enrollment_id: row?.course_enrollment_id,
      extraSessions: '',
      extraSaleAmount: '',
      remarks: '',
    },
    validationSchema,
    onSubmit: (values) => {
      const allocation = buildAllocation(Number(values.extraSessions));
      const payload = {
        ...values,
        updateFeePerSession,
        ...(subjects.length
          ? { subjectSessions: subjects.map((s, i) => ({ subject_enrollment_id: s.subject_enrollment_id, sessions: allocation[i] })) }
          : {}),
      };
      dispatch(addRetention(payload)).then((res) => {
        if (res.payload.status === 'success') {
          enqueueSnackbar(res.payload.message, {
            variant: 'success',
          });
          setOpen(false);
          formik.resetForm();
          if (apiCall) {
            dispatch(apiCall(dispatchParams));
          }
        } else {
          enqueueSnackbar(res.payload.message, {
            variant: 'error',
          });
        }
      });
    },
  });

  const extraSessionsNum = Number(formik.values.extraSessions);
  const hasValidExtra = Number.isInteger(extraSessionsNum) && extraSessionsNum > 0;
  const allocation = buildAllocation(hasValidExtra ? extraSessionsNum : 0);
  const allocatedTotal = allocation.reduce((a, n) => a + n, 0);
  const overAllocated = hasValidExtra && allocatedTotal > extraSessionsNum;
  const unallocated = hasValidExtra && subjects.length > 0 ? Math.max(extraSessionsNum - allocatedTotal, 0) : 0;

  const hasTypedAmount = formik.values.extraSaleAmount !== '' && !isNaN(Number(formik.values.extraSaleAmount));
  const totalSaleAmountAfterInr = hasTypedAmount
    ? Number(row?.total_sales_amount || 0) + Number(formik.values.extraSaleAmount)
    : null;

  // New fee per session = new total sale amount / new total sessions, both from cro_assignment (what the backend uses).
  const saleTotals = rateInfo?.saleTotals;
  const newFeePerSession =
    hasTypedAmount && hasValidExtra && saleTotals
      ? Math.floor(
          (Number(saleTotals.fees) + Number(formik.values.extraSaleAmount)) / (Number(saleTotals.sessions) + extraSessionsNum),
        )
      : null;

  return (
    <GlobalDialog
      title={'Add Upsell'}
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth={'sm'}
      closeButton={true}
      popupButton={
        <EditButton size="small" action={() => setOpen(true)}>
          Upsell
        </EditButton>
      }
      content={
        <Box>
          <Stack direction={'column'} spacing={0.5} sx={{ pb: 2 }}>
            <Typography variant="subtitle2">Current Sale Details</Typography>
            <Stack direction={'row'} spacing={4}>
              <Typography variant="body2" color="text.secondary">
                Current Sale Amount:{' '}
                <Typography component="span" variant="body2" fontWeight={600}>
                  {row?.total_sales_amount ?? '-'} INR
                </Typography>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Current Sessions:{' '}
                <Typography component="span" variant="body2" fontWeight={600}>
                  {/* sold total from cro_assignment (fetched when the dialog opens), not the grid row's subject-row sum */}
                  {!rateLoading && saleTotals ? saleTotals.sessions : '-'}
                </Typography>
              </Typography>
            </Stack>
          </Stack>
          <Divider sx={{ mb: 2 }} />
          {isAedPriced ? (
            <Stack direction={'column'} spacing={2}>
              <Alert severity="warning">
                Upsell isn't supported for {rateInfo.currencyLabel}-priced courses. Convert this course to INR
                first (Cart &gt; Convert to INR), or enroll the upsell as a new course.
              </Alert>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <CancelButton size="small" action={() => setOpen(false)}>
                  Close
                </CancelButton>
              </Box>
            </Stack>
          ) : (
            <FormikProvider value={formik}>
              <Form onSubmit={formik.handleSubmit}>
                <Stack direction={'column'} spacing={2}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Extra Sessions"
                    name="extraSessions"
                    value={formik.values.extraSessions}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.extraSessions && Boolean(formik.errors.extraSessions)}
                    helperText={formik.touched.extraSessions && formik.errors.extraSessions}
                  />
                  {subjects.length > 0 && !rateLoading ? (
                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                        <Typography variant="subtitle2">Sessions per subject</Typography>
                        {canChooseSplit ? (
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                              <Switch
                                size="small"
                                checked={distributeEqually}
                                onChange={(e) => handleToggleEqual(e.target.checked)}
                              />
                            }
                            label={<Typography variant="body2">Distribute equally</Typography>}
                          />
                        ) : null}
                      </Stack>
                      {subjects.map((s, i) => (
                        <Stack
                          key={s.subject_enrollment_id}
                          direction={'row'}
                          alignItems={'center'}
                          justifyContent={'space-between'}
                          sx={{ py: 0.5 }}
                        >
                          <Box>
                            <Typography variant="body2">{s.subject_name || `Subject ${s.subject_enrollment_id}`}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Fee per session: {Number(s.fee_per_session) > 0 ? `${Number(s.fee_per_session)} INR` : '-'}
                            </Typography>
                          </Box>
                          <Stack direction={'row'} spacing={1.5} alignItems={'center'}>
                            <Typography variant="body2" color="text.secondary">
                              {s.total_sessions}
                            </Typography>
                            {!equalMode ? (
                              <TextField
                                size="small"
                                label="Add"
                                sx={{ width: 90 }}
                                inputProps={{ inputMode: 'numeric' }}
                                value={manualSessions[s.subject_enrollment_id] ?? ''}
                                onChange={(e) =>
                                  setManualSessions((prev) => ({
                                    ...prev,
                                    [s.subject_enrollment_id]: e.target.value.replace(/\D/g, ''),
                                  }))
                                }
                              />
                            ) : null}
                            {hasValidExtra ? (
                              <Typography variant="body2" fontWeight={600}>
                                → {Number(s.total_sessions) + allocation[i]} (+{allocation[i]})
                              </Typography>
                            ) : null}
                          </Stack>
                        </Stack>
                      ))}
                      {overAllocated ? (
                        <Typography variant="caption" color="error">
                          Allocated {allocatedTotal} is more than the {extraSessionsNum} extra sessions.
                        </Typography>
                      ) : null}
                    </Box>
                  ) : null}
                  {unallocated > 0 && !rateLoading ? (
                    <Alert severity="info">
                      {unallocated} session{unallocated === 1 ? '' : 's'} not allocated to any subject yet. They count in
                      the sale and the total, but can't be scheduled until they are added to a subject.
                    </Alert>
                  ) : null}
                  <TextField
                    fullWidth
                    type="number"
                    label="Extra Sale Amount (INR)"
                    name="extraSaleAmount"
                    value={formik.values.extraSaleAmount}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.extraSaleAmount && Boolean(formik.errors.extraSaleAmount)}
                    helperText={formik.touched.extraSaleAmount && formik.errors.extraSaleAmount}
                  />
                  {totalSaleAmountAfterInr !== null ? (
                    <Typography variant="body2">
                      Total Sale Amount After:{' '}
                      <Typography component="span" variant="body2" fontWeight={600}>
                        {totalSaleAmountAfterInr} INR
                      </Typography>
                    </Typography>
                  ) : null}
                  <Box>
                    <FormControlLabel
                      sx={{ m: 0 }}
                      control={
                        <Switch
                          size="small"
                          checked={updateFeePerSession}
                          onChange={(e) => setUpdateFeePerSession(e.target.checked)}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {newFeePerSession !== null
                            ? `Also update fee per session to ${newFeePerSession} INR`
                            : 'Also update fee per session'}
                        </Typography>
                      }
                    />
                    <Typography variant="caption" color="text.secondary" display="block">
                      Applies to all subjects of this course. Off = the current fee per session stays as it is.
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    label="Remarks"
                    name="remarks"
                    multiline
                    rows={3}
                    value={formik.values.remarks}
                    onChange={formik.handleChange}
                  />
                </Stack>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
                  <Stack direction={'row'} spacing={1}>
                    <CancelButton size="small" action={() => setOpen(false)}>
                      Cancel
                    </CancelButton>
                    <SubmitButton disabled={loading || rateLoading || overAllocated} size="small" type="submit">
                      {loading ? 'Please wait...' : 'Save'}
                    </SubmitButton>
                  </Stack>
                </Box>
              </Form>
            </FormikProvider>
          )}
        </Box>
      }
    />
  );
};

export default AddRetention;
